import React, {useState} from 'react';
import gql from 'graphql-tag';
import {Route, Switch, useHistory, useLocation, useParams, useRouteMatch} from 'react-router';
import {useApolloClient, useQuery} from '@apollo/client';
import {GraphQLError} from 'graphql';

import {Attendee, DashboardNotification, EventSchedule} from '../../../schema';

import UnexpectedErrorScene from '../../system/UnexpectedErrorScene';
import NotFoundScene from '../../system/NotFoundScene';
import AttendeePermissions from '../../../models/attendee/AttendeePermissions'
import Schedule from '../../../models/event/Schedule'
import IconCertificate from '../../../components/icon/IconCertificate';
import IconEmail from '../../../components/icon/IconEmail';
import IconExternalLink from '../../../components/icon/IconExternalLink';
import IconTransfer from '../../../components/icon/IconTransfer';
import IconTrash from '../../../components/icon/IconTrash';
import ReactDocumentTitle from '../../../components/ReactDocumentTitle';
import AppLayout from '../../../layouts/AppLayout';
import OldUrls from '../../../models/utils/OldUrls';
import Avatar from '../../../ui/Avatar';
import DropdownActionMenu from '../../../ui/DropdownActionMenu';
import TextButton from '../../../ui/TextButton';
import H1 from '../../../ui/H1';
import DropdownMenuSection from '../../../ui/DropdownMenuSection'
import AttendeePaymentToggle from '../../../ui/AttendeePaymentToggle'
import AttendeeStatusSwitch from '../../../ui/AttendeeStatusSwitch'
import DropdownMenuItem from '../../../ui/DropdownMenuItem'
import AttendeeRegistrationStatus from '../../../ui/AttendeeRegistrationStatus'
import AttendeePaymentStatus from '../../../ui/AttendeePaymentStatus'
import AttendeeCertificateStatus from '../../../ui/AttendeeCertificateStatus'
import AttendeeEvaluationStatus from '../../../ui/AttendeeEvaluationStatus'
import UIContainer from '../../../ui/UIContainer';
import {showError, showInfo} from '../../../ui/ToastNotification';
import DeleteAttendeeDlg from '../../../components/attendee/DeleteAttendeeDlg';
import TransferDlg from '../../../components/attendee/TransferDlg';
import AttendeeDetailsSkeleton from './AttendeeDetailsSkeleton'
import Announcement from '../../../components/utils/Announcement';
import {reverse} from '../../../urls';
import ExtendedDropdownMenuItem from '../../../ui/ExtendedDropdownMenuItem';
import {Notifications} from '../../../models/utils/Notifications';
import {UserContext} from '../../../utils/Context';
import {AttendeeMenuNotice} from '../../../components/attendee/AttendeeMenuNotice';
import CertificateAvailability from '../../../models/event/CertificateAvailability';
import SendEmailDlg from "../../../components/attendee/SendEmailDlg";
import ContactInfoTab from "../../../components/attendee/ContactInfoTab";
import EvaluationTab from "../../../components/attendee/evaluation/EvaluationTab";
import {NavLink} from "react-router-dom";
import cn from "classnames";

interface QueryParams {
  id?: string
}

function getSchedule(schedule: EventSchedule) {
  return new Schedule({
    start: schedule.start,
    end: schedule.end,
    timezone: schedule?.timezone || null,
  })
}

function useSearchQuery() {
  const {search} = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}


export default function AttendeeDetailScene() {

  const {id} = useParams<QueryParams>();
  const searchQuery = useSearchQuery();
  const history = useHistory();
  const client = useApolloClient();
  const match = useRouteMatch();
  const {search} = useLocation();


  const [showDeleteDlg, setShowDeleteDlg] = useState(false);
  const [showTransferDlg, setShowTransferDlg] = useState(false);
  const [showSendEmailDlg, setShowSendEmailDlg] = useState(false);

  const onChangePayment = (state: boolean) => {
    client.mutate({
      mutation: attendeePaymentMutation,
      variables: {id, paid: state},
      optimisticResponse: {
        changeAttendeePaymentStatus: {
          id,
          __typename: 'Attendee',
          isPaid: state
        }
      }
    }).then(
      () => {
        showInfo('Payment status has been changed');
      },
      (apolloError) => {
        apolloError.graphQLErrors.map((error: GraphQLError) => showError(error.message));
      }
    );
  }

  const onChangeStatus = (status: any) => {
    client.mutate({
      mutation: attendeeParticipationMutation,
      variables: {id, status: status},
      optimisticResponse: () => {

        const cacheFragment: any = client.cache.readFragment({
          id: client.cache.identify({id, __typename: 'Attendee'}),
          fragment: gql`fragment attendeeSample on Attendee {
            permissions
            generateCertificateState
          }`
        })
        return {
          changeAttendeeParticipationStatus: {
            id,
            __typename: 'Attendee',
            status: status,
            permissions: cacheFragment.permissions,
            generateCertificateState: cacheFragment.generateCertificateState
          }
        }
      }
    }).then(
      () => {
        showInfo('Participation status has been changed');
      },
      (apolloError) => {
        // !FIXME: not only apollo error can be here
        apolloError.graphQLErrors.map((error: GraphQLError) => showError(error.message));
      }
    );
  }

  const onGenerateCertificate = (send = true) => {
    client.mutate({
      mutation: generateCertificateMutation,
      variables: {id, send: send},
      update: (cache, {data: {generateCertificate}}) => {
        cache.modify({
          id: cache.identify({id, __typename: 'Attendee'}),
          fields: {
            certificate() {
              return generateCertificate;
            }

          }
        });
      }
    }).then(
      () => {
        showInfo('Generation of certificate has started');
      },
      (apolloError) => {
        apolloError.graphQLErrors.map((error: GraphQLError) => showError(error.message));
      }
    );
  }

  const {loading, data, error} = useQuery<AttendeeDetailQuery>(query, {
    variables: {id, notificationId: Notifications.LIKE_NEW_ATTENDEE_PAGE}
  });

  if (error) {
    return <UnexpectedErrorScene
      error={error}
      errorInfo={null}
      eventId={null}
    />;
  }

  const goBack = () => {
    const defaultBack = () => history.goBack();
    switch (searchQuery.get('ref')) {
      case 'dashboard':
        history.push(reverse('dashboard'));
        break;
      case 'event':
        defaultBack();
        if (data?.attendee.event.id) {
          window.location.assign(OldUrls.eventAttendees(data?.attendee.event.id));
        } else {

        }
        break;
      case 'coordinator':
        window.location.assign('/brand/attendees');
        break;
      case 'trainer':
        window.location.assign('/trainer/attendees');
        break;
      default:
        defaultBack();
    }
  }


  const permissions = new AttendeePermissions(data?.attendee?.permissions);

  return (<>
      <ReactDocumentTitle title="Attendee"/>
      <AppLayout className="" fullScreen={true}>
        <UserContext.Consumer>
          {user => data?.notification.visible && <Announcement user={user}/>}
        </UserContext.Consumer>
        <div className="absolute h-24 w-24"></div>
        {loading && <AttendeeDetailsSkeleton/>}
        {!loading && (!data || !data.attendee) && <NotFoundScene/>}
        {!loading && !!data && data.attendee && (
          <>
            <div className="bg-gray-light">
              <UIContainer>
                <TextButton className="text-left pl-0 mt-4" onClick={goBack}>&larr; Back</TextButton>
                <div className="flex mt-1 pb-10">
                  <Avatar name={data.attendee.name} className="rounded-md w-28 h-28"/>
                  <div className="flex flex-1 flex-col ml-4 justify-between">
                    <H1>{data.attendee.name}</H1>
                    <div className="flex">
                      <AttendeeRegistrationStatus status={data.attendee.status} className="mr-10"/>
                      <AttendeePaymentStatus status={data.attendee.isPaid} isFree={data.attendee.event.isFree}
                                             className="mr-10"/>
                      <AttendeeEvaluationStatus evaluation={data.attendee.evaluation} evaluationUrl={
                        data.attendee.evaluation ? reverse('attendees_detail_evaluation', {id: data.attendee.id}) : undefined
                      } className="mr-10"/>
                      <AttendeeCertificateStatus
                        canGenerate={permissions.generateCertificate}
                        certificate={data.attendee.certificate}
                        state={data.attendee.generateCertificateState}
                        onGenerateClick={() => onGenerateCertificate()}
                        className="mr-10"/>
                    </div>
                    <div className="text-blue">
                      <IconEmail className="mr-1"/><a
                      href={"mailto:" + data.attendee.email}>{data.attendee.email}</a>
                    </div>
                  </div>
                  <div className="flex flex-col ml-2">
                    <DropdownActionMenu
                      caption={permissions.edit ? "Edit attendee" : undefined}
                      onButtonClick={permissions.edit ?
                        () => window.location.assign(OldUrls.editAttendee(
                          Number(data.attendee.event.id), Number(data.attendee.id), searchQuery.get('ref'))) : undefined}
                    >
                      <AttendeeMenuNotice event={data.attendee.event} status={data.attendee.status}
                                          state={data.attendee.generateCertificateState}/>
                      {!data.attendee.event.isFree && permissions.updatePayment && (
                        <DropdownMenuSection title="Payment">
                          <AttendeePaymentToggle isPaid={data.attendee.isPaid}
                                                 onChange={(isPaid) => onChangePayment(isPaid)}/>
                        </DropdownMenuSection>
                      )}
                      {permissions.updateParticipation && (
                        <DropdownMenuSection title="Status">
                          <AttendeeStatusSwitch
                            status={data.attendee.status}
                            confirmedEvent={data.attendee.event.confirmed}
                            futureEvent={getSchedule(data.attendee.event.schedule).future()}
                            onChange={(status) => onChangeStatus(status)}/>
                        </DropdownMenuSection>
                      )}
                      <DropdownMenuSection title="Communication">
                        {permissions.updateParticipation && (
                          <DropdownMenuItem caption="Transfer" onClick={() => setShowTransferDlg(true)}>
                            <IconTransfer className="text-gray-dark -ml-1 mr-3"/>
                          </DropdownMenuItem>
                        )}
                        {/*<DropdownMenuItem caption="Send Email" onClick={() => setShowSendEmailDlg(true)}>*/}
                        {/*  <IconEmail className="text-gray-dark -ml-1 mr-3"/>*/}
                        {/*</DropdownMenuItem>*/}
                        <DropdownMenuItem caption="Copy evaluation link" onClick={() => {
                          navigator.clipboard.writeText(data.attendee.evaluationUrl);
                          showInfo('Evaluation link copied to clipboard');
                        }}>
                          <IconExternalLink className="text-gray-dark -ml-1 mr-3"/>
                        </DropdownMenuItem>
                        {permissions.generateCertificate &&
                          CertificateAvailability.isAvailable(data.attendee.generateCertificateState) &&
                          (
                            <ExtendedDropdownMenuItem
                              caption={data.attendee.certificate?.generating ? "Generating certificate..." : "Generate certificate"}
                              icon={IconCertificate}
                              tooltip={'The certificate is generating. Please wait a bit'} id="generate_certificate"
                              disabled={data.attendee.certificate?.generating}
                              onClick={() => onGenerateCertificate()}
                            />
                          )}
                      </DropdownMenuSection>
                      {permissions.delete && (
                        <DropdownMenuSection className="pt-2 pb-0">
                          <DropdownMenuItem caption="Delete attendee" className="text-red"
                                            onClick={() => setShowDeleteDlg(true)}>
                            <IconTrash className="-ml-1 mr-3"/>
                          </DropdownMenuItem>
                        </DropdownMenuSection>
                      )}
                    </DropdownActionMenu>
                    <TextButton onClick={() =>
                      history.push(reverse('contacts_detail', {id: data?.attendee.contact.id}))
                    } className="text-left">View contact</TextButton>
                  </div>
                </div>
                <NavLink exact to={reverse('attendees_detail', {id: data.attendee.id}) + search}
                         className={(isActive: boolean) => cn("font-bold mr-6 outline-none py-2 inline-block",
                           {"border-b-4 border-blue text-black": isActive, "text-blue": !isActive})}
                >
                  Contact info
                </NavLink>
                <NavLink to={reverse('attendees_detail_evaluation', {id: data.attendee.id}) + search}
                         className={(isActive: boolean) => cn("font-bold mr-6 outline-none py-2  inline-block",
                           {"border-b-4 border-blue text-black": isActive, "text-blue": !isActive})}
                >
                  Evaluation
                </NavLink>
              </UIContainer>
            </div>
            <UIContainer className="pb-10">
              <Switch>
                <Route exact path={`${match.path}`}>
                  <ContactInfoTab attendee={data.attendee}/>
                </Route>
                <Route path={`${match.path}/evaluation`}>
                  <EvaluationTab evaluation={data.attendee.evaluation} attendee={data.attendee}
                                 eventTitle={data.attendee.event.title}/>
                </Route>
              </Switch>
              <DeleteAttendeeDlg id={data.attendee.id} eventId={data.attendee.event.id}
                                 name={data.attendee.name} show={showDeleteDlg} setShowDialog={setShowDeleteDlg}/>
              <TransferDlg id={data.attendee.id} eventId={data.attendee.event.id}
                           show={showTransferDlg} setShowDialog={setShowTransferDlg}/>
              <SendEmailDlg id={data.attendee.id} eventId={data.attendee.event.id} show={showSendEmailDlg}
                            setShowDialog={setShowSendEmailDlg}/>
            </UIContainer>
          </>
        )
        }
      </AppLayout>
    </>
  )
    ;
}

export type AttendeeScreenData =
  Pick<Attendee, "id" | "name" | "contact" | "email" | "status" | "created" | "phone" | "birthDate" |
    "memberId" | "consents" | "comment" | "customFields" | "source" | "permissions" | "isPaid" | "paidAmount" | "paidCurrency" |
    "referral" | "paymentMethod" | "bankCard" | "promoCode" | "ticketType" | "event" | "homeAddress" | "company" |
    "companyAddress" | "companyRole" | "billingAddress" | "billingTaxName" | "billingTaxNumber" | "certificate" |
    "generateCertificateState" | "evaluation" | "evaluationUrl" | "detailedPaidAmount">

export interface AttendeeDetailQuery {
  attendee: AttendeeScreenData
  notification: Pick<DashboardNotification, "id" | "type" | "visible">
}

const query = gql`
  query AttendeeDetailQuery($id: ID!, $notificationId: ID!) {

    attendee: getAttendee(attendeeId: $id) {
      id
      contact {
        id
      }
      name
      email
      status
      created

      phone
      birthDate
      memberId
      consents {
        content
        accepted
      }
      comment
      referral
      customFields {
        label
        value
      }

      source

      permissions

      isPaid
      paidAmount
      paidCurrency
      paymentMethod
      detailedPaidAmount {
        caption
        amount {
          currency
          amount
        }
        important
        subLines {
          caption
          amount {
            currency
            amount
          }
          important
        }
      }
      bankCard {
        brand
        last4
      }
      promoCode
      ticketType {
        id
        name
        amount
        currency
      }

      event {
        id
        title
        confirmed
        isFree
        schedule {
          start
          end
          timezone
          status
        }
      }

      homeAddress {
        postcode
        street1
        street2
        city
        country
      }

      company
      companyAddress {
        postcode
        street1
        street2
        city
        country
      }
      companyRole

      billingAddress {
        postcode
        street1
        street2
        city
        country
      }
      billingTaxName
      billingTaxNumber

      certificate {
        id
        url
        generating
      }
      generateCertificateState

      evaluation {
        id
        approved
        rating
        review
        consent
        testimonial
        content {
          label
          replies {
            fieldId
            label
            answer
            replyType
          }
        }
      }
      evaluationUrl
    }
    notification: notification(id: $notificationId) {
      id
      type
      visible
    }
  }
`;

const attendeePaymentMutation = gql`
  mutation ChangeAttendeePaymentStatus($id: ID!, $paid: Boolean!) {
    changeAttendeePaymentStatus(attendeeId: $id, paid: $paid) {
      id
      isPaid
    }
  }
`;

const attendeeParticipationMutation = gql`
  mutation ChangeAttendeeParticipationStatus($id: ID!, $status: AttendanceStatus!) {
    changeAttendeeParticipationStatus(attendeeId: $id, status: $status) {
      id
      status
      permissions
      generateCertificateState
    }
  }
`;

const generateCertificateMutation = gql`
  mutation GenerateCertificateMutation($id: ID!, $send: Boolean!) {
    generateCertificate(attendeeId: $id, send: $send) {
      id
      url
      generating
    }
  }
`;
